import React, { Fragment } from "react";
import { NavLink } from "react-router-dom";
import { Icon } from "@material-ui/core";
import TouchRipple from "@material-ui/core/ButtonBase";
import MatxVerticalNavExpansionPanel from "./MatxVerticalNavExpansionPanel";
import { makeStyles } from "@material-ui/core/styles";
import clsx from "clsx";
import useSettings from "../../hooks/useSettings";

const useStyles = makeStyles(() => ({
  navItem: {
    color: "#374151",
    borderRadius: 6,
    marginBottom: 4,
    transition: "all 150ms ease-in",
    textDecoration: "none",
    "&:hover": {
      backgroundColor: "#FFF5EB",
      color: "#F26B1D",
      "& $itemIcon": {
        color: "#F26B1D",
      },
    },
  },
  navItemActive: {
    backgroundColor: "#FDEBD6 !important",
    color: "#F26B1D !important",
    "& $itemIcon": {
      color: "#F26B1D",
    },
  },
  compactNavItem: {
    "& $itemText": {
      display: "none",
    },
  },
  ripple: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    minHeight: 44,
    padding: "0 12px 0 0",
  },
  itemIcon: {
    color: "#4B5563",
    width: 36,
    minWidth: 36,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  itemText: {
    fontSize: "0.875rem",
    fontWeight: 500,
    paddingLeft: 12,
    lineHeight: 1.3,
  },
  childText: {
    fontSize: "0.78rem",
    fontWeight: 400,
    paddingLeft: 0,
  },
  label: {
    color: "#9CA3AF",
    fontSize: "0.6875rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    marginTop: 18,
    marginBottom: 8,
    paddingLeft: 12,
  },
  bulletIcon: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: "#D1D5DB",
    marginLeft: 22,
    marginRight: 10,
    flexShrink: 0,
  },
}));

const MatxVerticalNav = ({ items }) => {
  const { settings } = useSettings();
  const { mode } = settings.layout1Settings.leftSidebar;
  const classes = useStyles();

  const renderLevels = (data, isChild = false) => {
    return data.map((item, index) => {
      if (item.type === "label") {
        return (
          <p
            key={index}
            className={clsx("px-2 uppercase sidenavHoverShow", classes.label)}
          >
            {item.label}
          </p>
        );
      }

      if (item.children) {
        return (
          <MatxVerticalNavExpansionPanel mode={mode} item={item} key={index}>
            {renderLevels(item.children, true)}
          </MatxVerticalNavExpansionPanel>
        );
      }

      if (item.type === "extLink") {
        return (
          <a
            key={index}
            href={item.path}
            className={clsx(
              "flex compactNavItem whitespace-pre overflow-hidden",
              classes.navItem,
              { [classes.compactNavItem]: mode === "compact" },
            )}
            rel="noopener noreferrer"
            target="_blank"
          >
            <TouchRipple name="child" className={clsx("nav-ripple", classes.ripple)}>
              {item.icon ? (
                <Icon className={clsx("text-18", classes.itemIcon)}>
                  {item.icon}
                </Icon>
              ) : (
                <span className={classes.bulletIcon} />
              )}
              <span
                className={clsx(
                  "sidenavHoverShow",
                  classes.itemText,
                  isChild && classes.childText,
                )}
              >
                {item.name}
              </span>
            </TouchRipple>
          </a>
        );
      }

      return (
        <NavLink
          key={index}
          to={item.path}
          activeClassName={classes.navItemActive}
          className={clsx(
            "flex compactNavItem whitespace-pre overflow-hidden",
            classes.navItem,
            { [classes.compactNavItem]: mode === "compact" },
          )}
        >
          <TouchRipple name="child" className={clsx("nav-ripple", classes.ripple)}>
            {item?.icon ? (
              <Icon className={clsx("text-18", classes.itemIcon)}>
                {item.icon}
              </Icon>
            ) : (
              <Fragment>
                <span
                  className={clsx(classes.bulletIcon, {
                    hidden: mode === "compact",
                  })}
                />
                <span
                  className={clsx("nav-bullet-text text-11", {
                    hidden: mode !== "compact",
                  })}
                >
                  {item.iconText || item.name?.slice(0, 2)}
                </span>
              </Fragment>
            )}
            <span
              className={clsx(
                "text-left sidenavHoverShow",
                classes.itemText,
                isChild && classes.childText,
              )}
            >
              {item.name}
            </span>
          </TouchRipple>
        </NavLink>
      );
    });
  };

  return <div className="navigation py-2">{renderLevels(items)}</div>;
};

export default React.memo(MatxVerticalNav);
